import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Download, ExternalLink, XCircle, Clock } from "lucide-react";
import { api, API, LOGO_URL } from "../lib/api";
import { Container } from "../components/ui-kit";

export default function StripeReturn() {
  const [params] = useSearchParams();
  const [state, setState] = useState("checking"); // checking | completed | pending | failed
  const [data, setData] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id") || localStorage.getItem("hh_stripe_session");
    if (!sessionId) { setState("failed"); return; }
    let timer;
    const poll = async () => {
      tries.current += 1;
      try {
        const { data: r } = await api.get(`/payments/stripe/status/${sessionId}`);
        if (r.status === "completed") {
          localStorage.removeItem("hh_stripe_session");
          setData(r); setState("completed"); return;
        }
        if (r.status === "failed") { setState("failed"); return; }
      } catch (e) { /* keep polling */ }
      if (tries.current >= 12) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params]);

  const campaign = localStorage.getItem("hh_stripe_campaign") || "your selected initiative";

  useEffect(() => {
    if (state === "completed" && data && window.gtag) {
      window.gtag("event", "purchase", {
        transaction_id: data.transaction_ref || data.receipt_number,
        value: data.amount,
        currency: data.currency,
        items: [{ item_name: campaign }],
      });
    }
  }, [state, data, campaign]);

  return (
    <div className="min-h-[70vh] bg-sand">
      <Container className="py-20">
        <div className="mx-auto max-w-lg rounded-3xl border border-emerald-900/10 bg-white p-10 text-center hh-shadow">
          <img src={LOGO_URL} alt="Helping Hands" className="mx-auto h-16 w-16 rounded-2xl bg-white object-contain p-1 ring-1 ring-gold/40" />

          {state === "checking" && (
            <div className="mt-6"><Loader2 className="mx-auto h-10 w-10 animate-spin text-emerald-700" /><p className="mt-4 font-display text-2xl font-bold text-emerald-900">Confirming your payment…</p><p className="mt-2 text-sm text-slate-500">Please wait while we verify your contribution securely.</p></div>
          )}

          {state === "completed" && (
            <div className="mt-6" data-testid="stripe-success">
              <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" />
              <h3 className="mt-4 font-display text-2xl font-bold text-emerald-900">Thank you for your support</h3>
              <p className="mt-1 text-sm text-slate-600">Your contribution to <b>{campaign}</b> has been recorded.</p>
              <div className="mt-6 rounded-2xl border border-emerald-900/10 bg-sand p-5 text-start">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Receipt Number</span><b className="text-emerald-900">{data.receipt_number}</b></div>
                <div className="mt-1.5 flex justify-between text-sm"><span className="text-slate-500">Reference</span><span className="text-emerald-900">{data.transaction_ref}</span></div>
                <div className="mt-4 flex flex-col gap-2">
                  <a href={`${API}/receipts/${data.receipt_number}/pdf`} target="_blank" rel="noreferrer" onClick={() => window.gtag && window.gtag("event", "receipt_download", { transaction_id: data.receipt_number, method: "stripe" })} data-testid="stripe-download-receipt" className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-800"><Download className="h-4 w-4" /> Download PDF Receipt</a>
                  <a href={`/verify-receipt/${data.receipt_number}`} className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-900/20 px-5 py-2.5 text-sm font-semibold text-emerald-900 hover:bg-emerald-50"><ExternalLink className="h-4 w-4" /> Verify Receipt</a>
                </div>
              </div>
              <a href="/" className="mt-5 inline-block text-sm font-semibold text-slate-500 hover:text-emerald-900">Back to Home</a>
            </div>
          )}

          {state === "pending" && (
            <div className="mt-6" data-testid="stripe-pending"><Clock className="mx-auto h-12 w-12 text-gold" /><p className="mt-4 font-display text-2xl font-bold text-emerald-900">Your payment is being confirmed</p><p className="mt-2 text-sm text-slate-500">We will email you once the transaction is verified. You can safely close this page.</p></div>
          )}

          {state === "failed" && (
            <div className="mt-6" data-testid="stripe-failed"><XCircle className="mx-auto h-12 w-12 text-red-500" /><p className="mt-4 font-display text-2xl font-bold text-emerald-900">Payment not completed</p><p className="mt-2 text-sm text-slate-500">No successful payment has been recorded. You can try again anytime.</p><a href="/programs" className="mt-5 inline-block rounded-full bg-emerald-900 px-6 py-2.5 text-sm font-bold text-white hover:bg-emerald-800">Back to Programs</a></div>
          )}
        </div>
      </Container>
    </div>
  );
}
